import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { Module } from '@/types/module.interfaces';

import { flattenModules } from './helpers/auth-flatten.helper';
import { generateAuthData } from './helpers/auth-parse.helper';
import { DynamicRouteService } from './dynamic-route.service';

import {
  setAuthToken,
  getAuthToken,
  removeAuthToken
} from './helpers/auth-storage.helper';
import { AuthState, LoginData } from '@/types/user.interfaces';
import { ApiResponse } from '@/types/api-response.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private dynamicRoute = inject(DynamicRouteService);

  private readonly loginUrl = 'api/auth/login';
  private readonly verifyUrl = '/api/auth/verify/me';

  private authState = signal<AuthState | null>(null);

  isAuthenticated = computed(() => !!this.authState());
  user = computed(() => this.authState()?.user);
  userRole = computed(() => this.authState()?.user?.roles?.[0] ?? null);
  modules = computed<Module[]>(() => this.authState()?.modules ?? []);
  flatFeatures = computed(() => flattenModules(this.modules()));

  constructor() {
    this.initializeApp();
  }

  private async initializeApp() {
    const token = getAuthToken();
    if (token) {
      try {
        await this.verifyMe(token);
      } catch (error) {
        this.logout();
      }
    }
  }

  async login(email: string, password: string): Promise<ApiResponse<LoginData>> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<LoginData>>(this.loginUrl, { email, password })
      );

      const data = generateAuthData(response.data);
      setAuthToken(data.token);
      this.authState.set(data);
      this.dynamicRoute.loadDynamicRoutes(data.modules);

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async verifyMe(token: string): Promise<ApiResponse<LoginData>> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<LoginData>>(this.verifyUrl, { token })
      );

      const data = generateAuthData(response.data);
      this.authState.set(data);
      this.dynamicRoute.loadDynamicRoutes(data.modules);

      return response;
    } catch (error) {
      console.error('VerifyMe Error:', error);
      throw error;
    }
  }

  logout(): void {
    removeAuthToken();
    this.authState.set(null);
    this.router.navigate(['/login']);
  }

  getAccessToken(): string | null {
    return this.authState()?.token ?? getAuthToken();
  }

  getUser(): AuthState | null {
    return this.authState();
  }
}
